import React, { useState } from 'react';
import { FiX, FiTrendingUp, FiMessageCircle } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useModalStore } from '../../stores';
import { SimpleFormField } from '../Common/FormField';
import { showToast } from '../../utils/toast';

const CreateSignalModal: React.FC = () => {
  const { closeModal, modalType } = useModalStore();
  const [type, setType] = useState<'signal' | 'post'>('signal');
  const [symbol, setSymbol] = useState('');
  const [price, setPrice] = useState('');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [target, setTarget] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [description, setDescription] = useState('');

  const createMutation = useApi.useCreateSignal({
    onSuccess: (data) => {
      showToast('success', data.signal.type === 'signal' ? 'Signal posted successfully!' : 'Post created successfully!');
      closeModal('create-signal');
      setType('signal');
      setSymbol('');
      setPrice('');
      setSide('buy');
      setTarget('');
      setStopLoss('');
      setDescription('');
      // Don't call refetch - mutation already updates cache optimistically
    },
    onError: (error: any) => {
      showToast('error', error.response?.data?.error || 'Failed to create post');
    },
  });

  if (modalType !== 'create-signal') return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (type === 'signal') {
      if (!symbol || !price || !description) {
        return;
      }
      createMutation.mutate({
        type: 'signal',
        symbol: symbol.toUpperCase(),
        price: parseFloat(price),
        side,
        target: target ? parseFloat(target) : undefined,
        stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
        description
      });
    } else {
      if (!description) {
        return;
      }
      createMutation.mutate({
        type: 'post',
        description
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create {type === 'signal' ? 'Signal' : 'Post'}</h2>
          <button
            onClick={() => closeModal('create-signal')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Type Selector */}
          <div className="flex gap-3 mb-6">
            <button
              type="button"
              onClick={() => setType('signal')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors ${
                type === 'signal'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FiTrendingUp className="h-5 w-5" />
              Trading Signal
            </button>
            <button
              type="button"
              onClick={() => setType('post')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-colors ${
                type === 'post'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FiMessageCircle className="h-5 w-5" />
              General Post
            </button>
          </div>

          {type === 'signal' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <SimpleFormField
                  label="Symbol"
                  name="symbol"
                  type="text"
                  value={symbol}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="AAPL"
                  required
                />
                <SimpleFormField
                  label="Price"
                  name="price"
                  type="number"
                  value={price}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(e.target.value)}
                  placeholder="185.50"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Side
                  </label>
                  <select
                    value={side}
                    onChange={(e) => setSide(e.target.value as 'buy' | 'sell')}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="buy">BUY</option>
                    <option value="sell">SELL</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <SimpleFormField
                  label="Target Price (Optional)"
                  name="target"
                  type="number"
                  value={target}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTarget(e.target.value)}
                  placeholder="195.00"
                />
                <SimpleFormField
                  label="Stop Loss (Optional)"
                  name="stopLoss"
                  type="number"
                  value={stopLoss}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStopLoss(e.target.value)}
                  placeholder="182.00"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Description {type === 'signal' && '*'}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={type === 'signal' ? 'Strong breakout above resistance with high volume. RSI shows bullish momentum.' : 'Share your thoughts...'}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[120px]"
              required={type === 'signal'}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => closeModal('create-signal')}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSignalModal;

